import { FC, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { PageLayout } from "@components";
import OrderFormSettingsContent from "./tabs/OrderFormSettingsContent";
import ApiKeysSettingsContent from "./tabs/ApiKeysSettingsContent";
import McpTokensSettingsContent from "./tabs/McpTokensSettingsContent";
import VehicleFeaturesSettingsContent from "./tabs/VehicleFeaturesSettingsContent";
import ResourceTypesSettingsContent from "./tabs/ResourceTypesSettingsContent";
import MyWorkspacesSettingsContent from "./tabs/MyWorkspacesSettingsContent";
import WorkspaceSettingsContent from "./tabs/WorkspaceSettingsContent";
import ProfileSettingsContent from "./tabs/ProfileSettingsContent";
import IntegrationsOverviewContent from "./tabs/IntegrationsOverviewContent";
import { SettingsRow } from "./components/SettingsRow";
import { cn } from "@design-system";
import { useWorkspace } from "../../context/WorkspaceContext";
import { useCanManageWorkspace } from "../../hooks/useCanManageWorkspace";

// Vertical tab item interface
interface VerticalTabItem {
  id: string;
  label: string;
  content: React.ComponentType<any>;
}

const SettingsPage: FC = () => {
  const { t } = useTranslation(["common", "order_form", "workspace"]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTabId, setActiveTabId] = useState("profile");
  const { activeWorkspace } = useWorkspace();
  const canManageWorkspace = useCanManageWorkspace(
    activeWorkspace?.workspaceId ?? "",
  );

  // Initialize active tab from URL params
  useEffect(() => {
    const tabParam = searchParams.get("tab");
    if (tabParam) {
      setActiveTabId(tabParam);
    }
  }, [searchParams]);

  const tabs: VerticalTabItem[] = [
    {
      id: "profile",
      label: t("workspace:settings.tabs.profile"),
      content: ProfileSettingsContent,
    },
    {
      id: "my-workspaces",
      label: t("workspace:settings.tabs.my_workspaces"),
      content: MyWorkspacesSettingsContent,
    },
    {
      id: "workspace",
      label: t("workspace:settings.tabs.workspace"),
      content: WorkspaceSettingsContent,
    },
    {
      id: "resource-types",
      label: t("workspace:settings.tabs.resource_types"),
      content: ResourceTypesSettingsContent,
    },
    {
      id: "vehicle-features",
      label: t("workspace:features.title"),
      content: VehicleFeaturesSettingsContent,
    },
    {
      id: "order-form",
      label: t("workspace:settings.tabs.order_form"),
      content: OrderFormSettingsContent,
    },
    {
      id: "api-keys",
      label: t("workspace:settings.tabs.api_keys"),
      content: ApiKeysSettingsContent,
    },
    {
      id: "integrations",
      label: t("workspace:settings.tabs.integrations"),
      content: IntegrationsOverviewContent,
    },
    {
      id: "mcp-tokens",
      label: t("workspace:settings.tabs.mcp_tokens"),
      content: McpTokensSettingsContent,
    },
    // Future tabs can be added here
    // {
    //   id: "notifications",
    //   label: "Varslinger",
    //   content: NotificationSettingsContent,
    // },
  ];

  // Administrative settings are hidden from non-managers; backend/rules
  // enforce write permissions independently.
  const visibleTabs = tabs.filter(
    (tab) =>
      !["workspace", "vehicle-features"].includes(tab.id) || canManageWorkspace,
  );

  const activeTab =
    visibleTabs.find((tab) => tab.id === activeTabId) || visibleTabs[0];
  const ActiveContent = activeTab.content;

  // Handle subtab for order-form
  const currentSubtab = searchParams.get("subtab") || "general";

  const handleTabClick = (tabId: string) => {
    setActiveTabId(tabId);

    // For order-form tab, always include default subtab
    if (tabId === "order-form") {
      setSearchParams({ tab: tabId, subtab: "general" });
    } else {
      setSearchParams({ tab: tabId });
    }
  };

  return (
    <PageLayout>
      <div className="space-y-6 max-w-7xl">
        {/* Settings Layout with Vertical Tabs */}
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Vertical Tab Navigation */}
          <div
            className={cn(
              "lg:w-56 flex-shrink-0 lg:pr-6",
              "lg:border-r border-border",
            )}
          >
            <nav className="space-y-1">
              {visibleTabs.map((tab) => {
                const isActive = tab.id === activeTab.id;

                return (
                  <button
                    key={tab.id}
                    onClick={() => handleTabClick(tab.id)}
                    className={`w-full px-3 py-2 text-left text-base rounded-md transition-colors duration-200 border-0 ${
                      isActive
                        ? "bg-brand-muted dark:bg-purple-900/20 text-brand dark:text-brand font-medium"
                        : "text-secondary hover:bg-brand-muted dark:hover:bg-purple-900/20"
                    }`}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Content Area */}
          <div className="flex-1 min-w-0">
            <ActiveContent
              SettingsRow={SettingsRow}
              {...(activeTabId === "order-form" && {
                currentSubtab,
                onSubtabChange: (subtab: string) => {
                  setSearchParams({ tab: "order-form", subtab });
                },
              })}
            />
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default SettingsPage;
